import { digitsOf, isJobBoundSource, type StartChatTarget } from '@chat-emp/shared';

/**
 * Where a draft for a not-yet-existing conversation lives.
 *
 * Keyed on the TARGET, not a channel id, because the entire premise is that
 * there is no channel. Derived from the two things that will eventually
 * determine one, so the draft survives anything that doesn't change who the
 * message is for: closing the widget, navigating the host, reloading the page.
 * That last one is not hypothetical — ajt2.5 is a multi-page app, so every link
 * an employer follows mid-compose is a full document load that takes the widget
 * with it.
 *
 * The key separates what the conversation would: the same pair talking about
 * two different jobs keeps two drafts. No channel id is composed (AI-3133).
 */
export function draftKeyFor(employerId: string, target: StartChatTarget): string {
  const job = isJobBoundSource(target.source) && target.job?.id ? target.job.id : '';
  return `compose-draft:${employerId}:${digitsOf(target.applicant.id)}:${job}`;
}

/**
 * sessionStorage, not localStorage: unsent text belongs to one sitting and must
 * not outlive the tab.
 *
 * Note which project rule this does NOT break — tokens are what may never be
 * persisted. A draft is the employer's own words, and losing it is the failure
 * mode worth designing against.
 */
export function readDraft(key: string): string {
  try {
    return sessionStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

export function writeDraft(key: string, text: string): void {
  try {
    sessionStorage.setItem(key, text);
  } catch {
    /* storage disabled or full — a lost draft must never break the widget */
  }
}

export function clearDraft(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
